import React, { useCallback, useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { registerLocale } from 'react-datepicker';
import Select from 'react-select';
import ptBR from 'date-fns/locale/pt-BR';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { useToast } from '../../../hooks/useToast';
import api from '../../../services/api';
import { DatePicker } from './styles';
import { useAuth } from '../../../hooks/auth';

registerLocale('ptBR', ptBR);

interface IResearch {
  id: string;
  title: string;
  status: string;
  description: string;
  syndicate_id: string;
  start_date: Date;
  finish_date: Date;
}

interface IProps {
  show: boolean;
  handleShow: (value: boolean) => void;
  handleResearches: (researches: IResearch[]) => void;
  handleLoading: (value: boolean) => void;
}
interface ISelect {
  value: string;
  label: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}
const AddResearchModal: React.FC<IProps> = ({
  show,
  handleShow,
  handleLoading,
  handleResearches,
}) => {
  const { addToast } = useToast();

  const [startDate, setStartDate] = useState<any>(null);
  const [finishDate, setFinishDate] = useState<any>(null);
  const [unions, setUnions] = useState<ISelect[]>([]);
  const { syndicate, user } = useAuth();
  const isSyndicateUser = user?.role === 'syndicate';
  const [union, setUnion] = useState<string | null>(
    user?.role === 'syndicate' ? syndicate?.id ?? null : null,
  );
  const history = useHistory();

  useEffect(() => {
    let mounted = true;
    const loadData = async () => {
      const loadedUnions = await api.get<ISyndicate[]>('/unions');

      if (mounted) {
        const parsedUnions = loadedUnions.data.map(syndicateMap => {
          return {
            label: syndicateMap.nome_fantasia,
            value: syndicateMap.id,
          };
        });

        setUnions([...parsedUnions]);
      }
    };

    if (mounted) {
      loadData();
    }

    return () => {
      mounted = false;
    };
  }, []);

  const handleClose = useCallback(() => {
    handleShow(false);
  }, [handleShow]);

  const handleCreateResearch = useCallback(
    async values => {
      try {
        handleLoading(true);

        const response = await api.post<IResearch>('/research', values);

        addToast({
          title: 'Sucesso',
          body: 'A pesquisa foi criada com sucesso.',
        });

        history.push(`/research/${response.data.id}`);

        const updatedResearches = await api.get('/research');
        handleResearches(updatedResearches.data);

        handleClose();
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao tentar criar uma nova pesquisa.',
        });
        console.error(error);
      } finally {
        handleLoading(false);
      }
    },
    [addToast, handleLoading, handleResearches, handleClose],
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Formik
        initialValues={{
          title: '',
          description: '',
          syndicate_id: union,
          start_date: '',
          finish_date: '',
        }}
        onSubmit={handleCreateResearch}
      >
        {({ handleSubmit, handleChange, values, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>Adicionar pesquisa</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col>
                  <Form.Group className="mb-3" controlId="formBasicEmail">
                    <Form.Label>Título *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Digite o título da pesquisa"
                      value={values.title}
                      onChange={handleChange('title')}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group
                    className="mb-3"
                    controlId="exampleForm.ControlTextarea1"
                  >
                    <Form.Label>Descrição</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      onChange={handleChange('description')}
                      value={values.description}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={12}>
                  <Form.Label>Sindicato</Form.Label>
                  <Select
                    className="w-100"
                    options={unions}
                    placeholder="Selecione um sindicato"
                    name="syndicate_id"
                    onChange={event => {
                      if (!isSyndicateUser) {
                        setUnion(event?.value || null);
                      }
                    }}
                    isClearable={!isSyndicateUser}
                    isDisabled={isSyndicateUser}
                    value={unions.find(option =>
                      isSyndicateUser
                        ? option.value === syndicate?.id
                        : option.value === union,
                    )}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Data de início</Form.Label>
                    <DatePicker
                      selected={startDate}
                      onChange={date => {
                        setFieldValue('start_date', date);
                        setStartDate(date);
                      }}
                      locale="ptBR"
                      timeInputLabel="Início:"
                      dateFormat="dd/MM/yyyy HH:mm"
                      showTimeInput
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Data de fim</Form.Label>
                    <DatePicker
                      selected={finishDate}
                      onChange={date => {
                        setFieldValue('finish_date', date);
                        setFinishDate(date);
                      }}
                      locale="ptBR"
                      timeInputLabel="Fim:"
                      dateFormat="dd/MM/yyyy HH:mm"
                      showTimeInput
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit">
                Salvar
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddResearchModal;
